import React from 'react';
import 'swiper/css';
import 'swiper/css/navigation';

import {
  Field,
  GetStaticComponentProps,
  useComponentProps,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { defaultLanguage } from 'temp/config';
import { ArticleCardData, ThemeData } from 'model/Article';
import ArticlesSectionComponent from './ArticlesSectionComponent';
import { useI18n } from 'next-localization';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import SliderNavCircle from 'components/Functional/Slider/SliderNavCircle';
import styles from './FeaturedThemeSection.module.scss';
import { articleService } from 'lib/services/ArticleService';
import RelatedArticleTile from 'components/Tiles/RelatedArticleTile';

type FeaturedThemeArticlesSectionFields = {
  viewAll?: Field<string>;
  baseUrl?: Field<string>;
  featuredTheme: Field<string>;
};

type FeaturedThemeArticlesSectionProps = ComponentProps & {
  fields?: FeaturedThemeArticlesSectionFields;
};

const Heading: React.FC<{ showAllSrc?: string; theme: ThemeData }> = ({ showAllSrc, theme }) => {
  const { t } = useI18n();
  return (
    <div className={`${styles.sliderHeading} mb-4 mb-md-5`}>
      <h4>{t('featuredThemeTitle')}</h4>
      <div className={styles.sliderHeadingCta}>
        {showAllSrc && (
          <ScLink
            field={{ value: { href: showAllSrc } }}
            className="inline-link revert-underline"
            hoverEnabled={false}
          >
            {t('viewAll')} {theme.name}
          </ScLink>
        )}
      </div>
      <h3>{theme.title.value}</h3>
      <div className={`d-none d-xl-flex ${styles.sliderNavigation} align-items-center ms-auto`}>
        <SliderNavCircle aria-label="Previous slide" className="nav-prev" direction="left" />
        <SliderNavCircle aria-label="Next slide" className="ms-3 nav-next" direction="right" />
      </div>
    </div>
  );
};

type ComponentLevelProps = {
  articles: ArticleCardData[];
  theme: ThemeData;
};

const FeaturedThemeSection: React.FC<FeaturedThemeArticlesSectionProps> = ({
  fields,
  rendering,
}) => {
  const props = useComponentProps<ComponentLevelProps>(rendering.uid);

  if (!props) return null;

  const { articles, theme } = props;

  if (!articles?.length) return null;

  return (
    <ArticlesSectionComponent
      rendering={rendering}
      articles={articles}
      backgroundColor="background-blue-grey"
      heading={<Heading showAllSrc={fields?.viewAll?.value} theme={theme} />}
      sliderCardComponent={RelatedArticleTile}
      getBadgeValue={(article) => article.articleTags?.targetItems?.[0].name.value}
    />
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const fields = rendering.fields as unknown as FeaturedThemeArticlesSectionFields;
  const siteId = layoutData.sitecore.context?.siteRoot;
  const language = layoutData.sitecore.context.language ?? defaultLanguage;
  const { featuredTheme } = fields;
  if (!featuredTheme?.value) return [];
  const [articles, theme] = await Promise.all([
    articleService.getFeaturedThemeArticles(language, siteId, featuredTheme.value),
    articleService.getTheme(featuredTheme.value, language),
  ]);
  return { articles, theme } as ComponentLevelProps;
};
export default FeaturedThemeSection;
